import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';

import { logger } from '@/utils/logger';
import { Button } from '@/components/ui/button';
import { Calendar, Eye, Mail, Send, Trash2, Users } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import ConfirmDialog from '@/components/ConfirmDialog';
import { Textarea } from '@/components/ui/textarea';
export default function AdminEmailCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [showClearHistory, setShowClearHistory] = useState(false);

  const clearHistory = async () => {
    try {
      await api.delete('/admin/email-campaigns/history');
      toast.success('Campaign history cleared');
      setShowClearHistory(false);
      fetchCampaigns();
    } catch (err) {
      logger.error('Clear campaign history failed:', err);
      toast.error('Failed to clear history');
    }
  };
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    recipient_filter: 'all'
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/admin/email-campaigns');
      setCampaigns(response.data);
    } catch (error) {
      logger.error('Error fetching campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCampaign = async () => {
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }

    setSending(true);
    try {
      // Plain text version for better deliverability
      const plainText = formData.message;
      
      // Convert message to beautiful Mementa-themed HTML
      const html_content = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&display=swap" rel="stylesheet">
        </head>
        <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background: #FFFAF5;">
          <div style="max-width: 600px; margin: 0 auto; background: white;">
            <!-- Header with Mementa branding -->
            <div style="background: linear-gradient(135deg, #20212B 0%, #2D3748 100%); padding: 40px 30px; text-align: center; border-radius: 0 0 30px 30px;">
              <div style="display: inline-block; background: #FED7A5; width: 60px; height: 60px; border-radius: 50%; margin-bottom: 20px; display: flex; align-items: center; justify-content: center;">
                <span style="font-size: 32px;">🎁</span>
              </div>
              <h1 style="margin: 0; color: #FED7A5; font-family: 'Playfair Display', serif; font-size: 28px; font-weight: 700;">
                Mementa
              </h1>
              <p style="margin: 8px 0 0 0; color: #D1D5DB; font-size: 14px; letter-spacing: 1px;">
                THOUGHTFUL GIFT DISCOVERY
              </p>
            </div>

            <!-- Main Content -->
            <div style="padding: 50px 30px;">
              <h2 style="margin: 0 0 24px 0; color: #20212B; font-family: 'Playfair Display', serif; font-size: 32px; font-weight: 700; line-height: 1.2;">
                ${formData.subject}
              </h2>
              
              <div style="color: #534145; font-size: 16px; line-height: 1.8; white-space: pre-wrap; margin-bottom: 40px;">
                ${formData.message}
              </div>

              <!-- CTA Button -->
              <div style="text-align: center; margin: 40px 0;">
                <a href="https://mementa.gifts/browse" style="display: inline-block; background: #9E6752; color: white; text-decoration: none; padding: 16px 40px; border-radius: 30px; font-weight: 600; font-size: 16px; box-shadow: 0 4px 12px rgba(158, 103, 82, 0.3);">
                  Explore Gifts
                </a>
              </div>

              <!-- Decorative divider -->
              <div style="border-top: 2px solid #F8F4F1; margin: 40px 0;"></div>

              <!-- Feature highlights -->
              <div style="text-align: center; margin: 30px 0;">
                <table width="100%" cellpadding="0" cellspacing="0" style="margin: 0;">
                  <tr>
                    <td style="padding: 15px; text-align: center; vertical-align: top; width: 33.33%;">
                      <div style="font-size: 28px; margin-bottom: 8px;">💝</div>
                      <div style="font-size: 12px; color: #73766A; font-weight: 600;">Curated Gifts</div>
                    </td>
                    <td style="padding: 15px; text-align: center; vertical-align: top; width: 33.33%;">
                      <div style="font-size: 28px; margin-bottom: 8px;">✨</div>
                      <div style="font-size: 12px; color: #73766A; font-weight: 600;">Personalized</div>
                    </td>
                    <td style="padding: 15px; text-align: center; vertical-align: top; width: 33.33%;">
                      <div style="font-size: 28px; margin-bottom: 8px;">💌</div>
                      <div style="font-size: 12px; color: #73766A; font-weight: 600;">Thoughtful</div>
                    </td>
                  </tr>
                </table>
              </div>
            </div>

            <!-- Footer -->
            <div style="background: #F8F4F1; padding: 30px; text-align: center; border-radius: 30px 30px 0 0;">
              <p style="margin: 0 0 15px 0; color: #534145; font-size: 14px; line-height: 1.6;">
                You're receiving this because you have an account with Mementa
              </p>
              
              <div style="margin: 20px 0;">
                <a href="https://mementa.gifts" style="color: #9E6752; text-decoration: none; margin: 0 10px; font-size: 14px; font-weight: 600;">Browse Gifts</a>
                <span style="color: #D1D5DB;">•</span>
                <a href="https://mementa.gifts/profile" style="color: #9E6752; text-decoration: none; margin: 0 10px; font-size: 14px; font-weight: 600;">Preferences</a>
              </div>

              <div style="border-top: 1px solid #E5E7EB; margin: 25px 0; padding-top: 25px;">
                <p style="margin: 0 0 8px 0; color: #9CA3AF; font-size: 12px;">
                  © 2025 Mementa. Made with love for thoughtful gift-givers.
                </p>
                <p style="margin: 0; color: #9CA3AF; font-size: 11px;">
                  <a href="{UNSUBSCRIBE_LINK}" style="color: #9E6752; text-decoration: underline;">Unsubscribe</a> or 
                  <a href="https://mementa.gifts/profile" style="color: #9E6752; text-decoration: underline;">update preferences</a>
                </p>
              </div>
            </div>
          </div>
        </body>
        </html>
      `;

      const response = await api.post('/admin/email-campaigns/send', null, {
        params: {
          subject: formData.subject,
          html_content,
          recipient_filter: formData.recipient_filter
        }
      });

      toast.success(`Campaign sent to ${response.data.recipient_count} recipients!`);
      setFormData({ subject: '', message: '', recipient_filter: 'all' });
      fetchCampaigns();
    } catch (error) {
      logger.error('Error sending campaign:', error);
      toast.error('Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  const getRecipientFilterLabel = (filter) => {
    switch (filter) {
      case 'all': return 'All Users';
      case 'has_wishlist': return 'Users with Wishlist Items';
      case 'birthday_this_month': return 'Birthday Reminders';
      default: return filter;
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-40 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}>
            Email Campaigns
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Send promotional emails and announcements to your users
          </p>
        </div>
      </div>

      {/* Compose New Campaign */}
      <div className="bg-white rounded-xl p-4 sm:p-6 mb-6 shadow-sm">
        <h2 className="text-xl font-semibold mb-4" style={{ color: '#20212B' }}>
          Compose Campaign
        </h2>

        <div className="space-y-4">
          <div>
            <Label>Subject Line *</Label>
            <Input
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="🎉 Holiday Sale - 20% Off Everything!"
              className="mt-1"
            />
          </div>

          <div>
            <Label>Message *</Label>
            <Textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Write your email message here...

Example:
Happy Holidays from Mementa! 🎄

We're excited to offer you 20% off all gifts through the end of the year. Find the perfect present for your loved ones.

Use code: HOLIDAY20 at checkout.

Happy shopping!"
              rows={10}
              className="mt-1"
            />
          </div>

          <div>
            <Label>Send To</Label>
            <Select
              value={formData.recipient_filter}
              onValueChange={(value) => setFormData({ ...formData, recipient_filter: value })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    All Users
                  </div>
                </SelectItem>
                <SelectItem value="has_wishlist">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Users with Wishlist Items
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              onClick={() => setShowPreview(true)}
              variant="outline"
              className="flex-1 flex items-center justify-center gap-2"
              disabled={!formData.subject || !formData.message}
            >
              <Eye className="w-4 h-4" />
              Preview
            </Button>
            <Button
              onClick={() => setShowConfirm(true)}
              className="flex-1 flex items-center justify-center gap-2"
              style={{ background: '#9E6752', color: 'white' }}
              disabled={sending || !formData.subject || !formData.message}
            >
              <Send className="w-4 h-4" />
              {sending ? 'Sending...' : 'Send Campaign'}
            </Button>
          </div>
        </div>
      </div>

      {/* Campaign History */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold" style={{ color: '#20212B' }}>
            Campaign History
          </h2>
          {campaigns.length > 0 && (
            <Button
              data-testid="clear-campaign-history-btn"
              variant="outline"
              size="sm"
              onClick={() => setShowClearHistory(true)}
              className="text-red-600 border-red-300 hover:bg-red-50 text-xs"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Clear History
            </Button>
          )}
        </div>

        {campaigns.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No campaigns sent yet</p>
        ) : (
          <div className="space-y-3">
            {campaigns.map((campaign) => (
              <div
                key={campaign.id}
                className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{campaign.subject}</h3>
                    <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {campaign.recipient_count} recipients
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {getRecipientFilterLabel(campaign.recipient_filter)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(campaign.sent_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
          </DialogHeader>
          <div className="mt-4" style={{ background: '#f5f5f5', padding: '20px' }}>
            <div style={{ maxWidth: '600px', margin: '0 auto', background: 'white' }}>
              {/* Header */}
              <div style={{ background: 'linear-gradient(135deg, #20212B 0%, #2D3748 100%)', padding: '40px 30px', textAlign: 'center', borderRadius: '0 0 30px 30px' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#FED7A5', width: '60px', height: '60px', borderRadius: '50%', marginBottom: '20px' }}>
                  <span style={{ fontSize: '32px' }}>🎁</span>
                </div>
                <h1 style={{ margin: 0, color: '#FED7A5', fontFamily: "'Playfair Display', serif", fontSize: '28px', fontWeight: 700 }}>
                  Mementa
                </h1>
                <p style={{ margin: '8px 0 0 0', color: '#D1D5DB', fontSize: '14px', letterSpacing: '1px' }}>
                  THOUGHTFUL GIFT DISCOVERY
                </p>
              </div>

              {/* Content */}
              <div style={{ padding: '50px 30px' }}>
                <h2 style={{ margin: '0 0 24px 0', color: '#20212B', fontFamily: "'Playfair Display', serif", fontSize: '32px', fontWeight: 700, lineHeight: '1.2' }}>
                  {formData.subject}
                </h2>
                <div style={{ color: '#534145', fontSize: '16px', lineHeight: '1.8', whiteSpace: 'pre-wrap', marginBottom: '40px' }}>
                  {formData.message}
                </div>
                <div style={{ textAlign: 'center', margin: '40px 0' }}>
                  <a href="#" style={{ display: 'inline-block', background: '#9E6752', color: 'white', textDecoration: 'none', padding: '16px 40px', borderRadius: '30px', fontWeight: 600, fontSize: '16px', boxShadow: '0 4px 12px rgba(158, 103, 82, 0.3)' }}>
                    Explore Gifts
                  </a>
                </div>

                <div style={{ borderTop: '2px solid #F8F4F1', margin: '40px 0' }}></div>

                {/* Features */}
                <div style={{ textAlign: 'center', margin: '30px 0', display: 'flex', justifyContent: 'space-around' }}>
                  <div style={{ padding: '15px', textAlign: 'center' }}>
                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>💝</div>
                    <div style={{ fontSize: '12px', color: '#73766A', fontWeight: 600 }}>Curated Gifts</div>
                  </div>
                  <div style={{ padding: '15px', textAlign: 'center' }}>
                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>✨</div>
                    <div style={{ fontSize: '12px', color: '#73766A', fontWeight: 600 }}>Personalized</div>
                  </div>
                  <div style={{ padding: '15px', textAlign: 'center' }}>
                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>💌</div>
                    <div style={{ fontSize: '12px', color: '#73766A', fontWeight: 600 }}>Thoughtful</div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{ background: '#F8F4F1', padding: '30px', textAlign: 'center', borderRadius: '30px 30px 0 0' }}>
                <p style={{ margin: '0 0 15px 0', color: '#534145', fontSize: '14px', lineHeight: '1.6' }}>
                  Find the perfect gifts with care and thoughtfulness
                </p>
                <div style={{ margin: '20px 0', fontSize: '14px' }}>
                  <a href="#" style={{ color: '#9E6752', textDecoration: 'none', margin: '0 10px', fontWeight: 600 }}>Browse Gifts</a>
                  <span style={{ color: '#D1D5DB' }}>•</span>
                  <a href="#" style={{ color: '#9E6752', textDecoration: 'none', margin: '0 10px', fontWeight: 600 }}>My Keepsakes</a>
                  <span style={{ color: '#D1D5DB' }}>•</span>
                  <a href="#" style={{ color: '#9E6752', textDecoration: 'none', margin: '0 10px', fontWeight: 600 }}>Preferences</a>
                </div>
                <div style={{ borderTop: '1px solid #E5E7EB', margin: '25px 0', paddingTop: '25px' }}>
                  <p style={{ margin: '0 0 8px 0', color: '#9CA3AF', fontSize: '12px' }}>
                    © 2025 Mementa. Made with love for thoughtful gift-givers.
                  </p>
                  <p style={{ margin: 0, color: '#9CA3AF', fontSize: '11px' }}>
                    Don't want to receive these emails? <a href="#" style={{ color: '#9E6752', textDecoration: 'underline' }}>Unsubscribe</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Confirmation */}
      <ConfirmDialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
        onConfirm={handleSendCampaign}
        title="Send Email Campaign?"
        description={`This will send "${formData.subject}" to ${getRecipientFilterLabel(formData.recipient_filter).toLowerCase()}.`}
        confirmText="Send Now"
        variant="primary"
      />
      <ConfirmDialog
        open={showClearHistory}
        onOpenChange={setShowClearHistory}
        onConfirm={clearHistory}
        title="Clear campaign history?"
        description="This permanently deletes all sent-campaign records. It does not affect subscribers or unsend any emails."
        confirmText="Clear History"
      />
    </div>
  );
}
